// client/src/components/pages/Geofencing/LiveTrackingMap.tsx (converted from .js, ST-00244)
// LexiStar live map (ST-00118, Phase 1) — MapLibre GL on the shared LexiStarMap foundation.
//
// Vehicles are the shared asset marker (map/assetMarker, ST-00244 option C): a disc in the
// status colour (vehicleStatus.js) with an upright truck or loader, a nose on the heading while
// moving, a plain disc when stopped and a hollow dashed disc when the position is stale. They
// are drawn as three symbol layers per source (map/assetMarkerImages: shadow, nose, face).
//
// Clustering (ST-00244): below zoom 16 vehicles close together merge into a counted bubble;
// clicking a bubble zooms in until it splits. The selected vehicle is in its own,
// never-clustered source (liveMapVehicles), so it is never hidden inside a bubble. The
// All | Fleet | Loaders filter is applied by the page before the vehicles reach this map, so
// bubbles only ever count what the filter shows.
//
// Positions glide: on each update a per-vehicle rAF tween runs from the currently-rendered
// coordinate to the new one and pushes interpolated frames to the sources with setData(), so
// vehicles never jump. Zones (polygon + circular-as-polygon), aerial image overlays and the
// vehicle trail are separate sources / layers. The prop contract is unchanged, so LiveView.tsx
// only had to drop its typed cast.
//
// ST-00276 phase 2 (map colours & states): `displays` (the status board rows' display, by
// registration) and `displayRules` (the organisation's rule set) decide each vehicle's colour and
// overlay marks; a vehicle without a row is drawn by the rules over its motion. The marks are
// circle layers around the marker (map/assetDisplay: a ring, a pulsing ring — steady with reduced
// motion — and a badge dot); the map key lists the colours, your own rules and the marks.
import { useState, useEffect, useRef, useMemo, useCallback, useId } from 'react';
import type { ComponentType } from 'react';
import { Source, Layer, Popup, Marker } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { GeoJSONSource, LngLatBoundsLike, Map as MaplibreMap } from 'maplibre-gl';
import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';
import { BasemapToggle as BasemapToggleJs } from '../../map/LexiStarMap';
import {
  circleToPolygonRing as circleToPolygonRingJs,
  polygonRingFromLatLngs as polygonRingFromLatLngsJs,
  geofenceCentroid as geofenceCentroidJs,
  isInsideGeofence as isInsideGeofenceJs,
  boundsFromLngLats as boundsFromLngLatsJs,
  lerpAngle as lerpAngleJs,
} from '../../map/geo';
import {
  ZoneTypeBadge as ZoneTypeBadgeJs,
  getZoneType as getZoneTypeJs,
  getZoneTypeMeta as getZoneTypeMetaJs,
} from './zoneTypes';
import { liveStatusOf, normaliseRegistration } from './vehicleStatus';
import AssetInfoCard, { ASSET_INFO_POPUP_CLASS } from '../../map/AssetInfoCard';
import LiveMapLegend from './LiveMapLegend';
import type { AssetStatusKey } from '../../map/assetStatus';
import usePulseAnimation from '../../map/usePulseAnimation';
import DisplayMarks from '../../map/DisplayMarks';
import {
  installDisplayMarkImages,
  compiledOf,
  displayMarkLayers,
  displayOfMotion,
  pulseLayerId,
} from '../../map/assetDisplay';
import type { AssetDisplay } from '../../map/displayRules';
import type { BoardDisplayRules } from '../../../types/lexistar-display-rules';
import {
  NOT_CLUSTER,
  assetClusterLayer,
  assetMarkerLayers,
  assetMarkerSelectionLayer,
  installAssetMarkerImages,
} from '../../map/assetMarkerImages';
import {
  EMPTY_VEHICLES,
  LIVE_MARKER_PX,
  buildVehicleCollections,
  deriveStatus,
  isStale,
  vehicleMeta,
  type RenderedPosition,
  type VehicleMeta,
} from './liveMapVehicles';
import api from '../../../services/api';
import { LexiStarMap } from '../../../types/commonComponents';
import type { AerialOverlayRow } from '../../../types/clientPages/aerialOverlays';
import type {
  GeofenceRow,
  LatLngPair,
  LiveVehiclePosition,
} from '../../../types/clientPages/geofencing';

// ─── Typed views of the JavaScript modules ───────────────────────────────────────────────────
type Ring = [number, number][];
const circleToPolygonRing = circleToPolygonRingJs as unknown as (
  lng: number | null,
  lat: number | null,
  radiusMeters: number
) => Ring | null;
const polygonRingFromLatLngs = polygonRingFromLatLngsJs as unknown as (
  coords: LatLngPair[] | null
) => Ring | null;
const geofenceCentroid = geofenceCentroidJs as unknown as (
  g: GeofenceRow
) => [number | null, number | null];
const isInsideGeofence = isInsideGeofenceJs as unknown as (
  lat: number,
  lng: number,
  g: GeofenceRow
) => boolean;
const boundsFromLngLats = boundsFromLngLatsJs as unknown as (
  pts: [number, number][]
) => LngLatBoundsLike | null;
const lerpAngle = lerpAngleJs as unknown as (a: number, b: number, t: number) => number;
const getZoneType = getZoneTypeJs as unknown as (zone: GeofenceRow) => string;
const getZoneTypeMeta = getZoneTypeMetaJs as unknown as (id: string) => { color: string };
const ZoneTypeBadge = ZoneTypeBadgeJs as unknown as ComponentType<{ type: string }>;
type Basemap = 'street' | 'satellite';
const BasemapToggle = BasemapToggleJs as unknown as ComponentType<{
  basemap: Basemap;
  onChange: (b: Basemap) => void;
}>;

// ─── Tuning ──────────────────────────────────────────────────────────────────────────────────
const ANIM_MS = 1000; // glide duration per position update
const SELECT_ZOOM = 17;
/** Vehicles cluster up to and including this zoom; from the next zoom on every one shows. */
export const CLUSTER_MAX_ZOOM = 15;
/** Vehicles closer than this (px) merge into a bubble. */
export const CLUSTER_RADIUS = 44;
const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

// Layer ids. The faces and the bubbles are clickable (the nose and shadow sit under the face).
const VEHICLES = 'vehicles';
const SELECTED = 'vehicle-selected';
const CLUSTERS = 'vehicle-clusters';
const OTHER_LAYERS = assetMarkerLayers(VEHICLES, { filter: NOT_CLUSTER });
const SELECTED_LAYERS = assetMarkerLayers(SELECTED);
// ST-00276: the display rules' marks around the markers (under: ring, pulse; over: badge).
const OTHER_MARKS = displayMarkLayers(VEHICLES, { size: LIVE_MARKER_PX, filter: NOT_CLUSTER });
const SELECTED_MARKS = displayMarkLayers(SELECTED, { size: LIVE_MARKER_PX });
const PULSE_LAYERS = [pulseLayerId(VEHICLES), pulseLayerId(SELECTED)];
const SELECTED_RING = assetMarkerSelectionLayer(`${SELECTED}-ring`, { radius: 17 });
const CLUSTER_LAYER = assetClusterLayer(CLUSTERS);
const INTERACTIVE_LAYERS = [`${VEHICLES}-face`, `${SELECTED}-face`, CLUSTERS, 'zones-fill'];

/** Stored polygon coordinates (JSON text or an already-parsed array) as [lat, lng] pairs. */
function polygonCoords(g: GeofenceRow): LatLngPair[] | null {
  const c = g.Coordinates;
  if (Array.isArray(c)) return c;
  if (typeof c !== 'string' || !c) return null;
  try {
    const parsed: unknown = JSON.parse(c);
    return Array.isArray(parsed) ? (parsed as LatLngPair[]) : null;
  } catch {
    return null;
  }
}

/** Where a zone's occupancy badge goes: the top edge of the zone, above its centre. */
function zoneLabelPoint(g: GeofenceRow): [number | null, number | null] {
  const [ln, la] = geofenceCentroid(g);
  if (ln == null || la == null) return [ln, la];
  if (g.GeofenceType === 'POLYGON') {
    const coords = polygonCoords(g);
    if (coords && coords.length >= 3) return [ln, Math.max(...coords.map(([lat]) => lat))];
    return [ln, la];
  }
  return [ln, la + (g.RadiusMeters || 0) / 111320]; // metres → degrees of latitude
}

// Aerial overlay placement (ST-00125). Overlays uploaded from projected GeoTIFFs carry
// `corners` — the reprojected WGS84 quad [[lng,lat] x4: TL,TR,BR,BL] — which MapLibre renders
// directly, keeping the slight grid-convergence rotation that an axis-aligned rectangle would
// lose (metres of misplacement at the edges). Legacy rows fall back to the bounds rectangle.
type Quad = [[number, number], [number, number], [number, number], [number, number]];
function overlayQuad(ov: AerialOverlayRow): Quad {
  let c: unknown = ov.corners;
  if (typeof c === 'string') {
    try {
      c = JSON.parse(c);
    } catch {
      c = null;
    }
  }
  if (
    Array.isArray(c) &&
    c.length === 4 &&
    c.every(
      (p: unknown) =>
        Array.isArray(p) &&
        p.length === 2 &&
        Number.isFinite(Number(p[0])) &&
        Number.isFinite(Number(p[1]))
    )
  ) {
    return (c as [unknown, unknown][]).map((p) => [Number(p[0]), Number(p[1])]) as Quad;
  }
  return [
    [ov.bounds_west, ov.bounds_north],
    [ov.bounds_east, ov.bounds_north],
    [ov.bounds_east, ov.bounds_south],
    [ov.bounds_west, ov.bounds_south],
  ];
}

// ─── Vehicle trail ───────────────────────────────────────────────────────────────────────────
// Last 4h of positions as a dashed line with start (green) / end (blue) dots.
interface TrailPoint {
  lat: number;
  lng: number;
}

function TrailLayer({ regNo }: { regNo: string }) {
  const [trail, setTrail] = useState<TrailPoint[]>([]);

  useEffect(() => {
    if (!regNo) return undefined;
    let live = true;
    const endTime = new Date().toISOString();
    const startTime = new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
    api.vehicleLocations
      .getTrail(regNo, { startTime, endTime })
      .then((res: { data?: { data?: TrailPoint[] } }) => {
        if (live) setTrail(res.data?.data || []);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [regNo]);

  if (trail.length < 2) return null;
  const coords = trail.map((p) => [p.lng, p.lat] as [number, number]);
  const line: Feature<LineString> = {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: coords },
  };
  const start = coords[0]!;
  const end = coords[coords.length - 1]!;

  return (
    <>
      <Source id="trail" type="geojson" data={line}>
        <Layer
          id="trail-line"
          type="line"
          layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          paint={{
            'line-color': '#0ea5e9',
            'line-width': 3,
            'line-opacity': 0.75,
            'line-dasharray': [2, 1.5],
          }}
        />
      </Source>
      <Marker longitude={start[0]} latitude={start[1]} style={{ pointerEvents: 'none' }}>
        <div className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow" />
      </Marker>
      <Marker longitude={end[0]} latitude={end[1]} style={{ pointerEvents: 'none' }}>
        <div className="w-3 h-3 rounded-full bg-sky-500 border-2 border-white shadow" />
      </Marker>
    </>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────────────────────
export interface LiveTrackingMapProps {
  vehicles?: LiveVehiclePosition[];
  geofences?: GeofenceRow[];
  isFullscreen: boolean;
  overlaysVersion: number;
  selectedRegNo?: string | null;
  onSelectVehicle?: (regNo: string) => void;
  flyToNonce?: number;
  /**
   * LexiStar status keys by normalised registration (useLexiStarStatusKeys): a vehicle with one
   * takes its colour from it, the rest from their motion (ST-00253).
   */
  statusKeys?: ReadonlyMap<string, AssetStatusKey>;
  /** ST-00276: the status board rows' display by normalised registration (useLexiStarDisplays). */
  displays?: ReadonlyMap<string, AssetDisplay>;
  /** ST-00276: the organisation's display rules (the board's display_rules): legend and fallback. */
  displayRules?: BoardDisplayRules | null;
  /** The vehicle card's Details button: open the detail panel (ST-00270). */
  onOpenDetails?: (regNo: string) => void;
}

const NO_KEYS: ReadonlyMap<string, AssetStatusKey> = new Map();
const NO_DISPLAYS: ReadonlyMap<string, AssetDisplay> = new Map();

interface Tween {
  fromLng: number;
  fromLat: number;
  toLng: number;
  toLat: number;
  fromHeading: number;
  toHeading: number;
  start: number;
  duration: number;
}

interface ZonePopup {
  lng: number;
  lat: number;
  geofence: GeofenceRow;
}

/** Zoom into a cluster bubble until it splits. */
export async function zoomIntoCluster(
  map: MaplibreMap,
  sourceId: string,
  clusterId: number,
  center: [number, number]
): Promise<void> {
  const src = map.getSource<GeoJSONSource>(sourceId);
  if (!src) return;
  try {
    const zoom = await src.getClusterExpansionZoom(clusterId);
    map.easeTo({ center, zoom: Math.max(zoom, map.getZoom() + 1), duration: 500 });
  } catch {
    /* the cluster went away (data moved on) */
  }
}

export default function LiveTrackingMap({
  vehicles = [],
  geofences = [],
  isFullscreen,
  overlaysVersion,
  selectedRegNo = null,
  onSelectVehicle,
  flyToNonce = 0,
  statusKeys = NO_KEYS,
  displays = NO_DISPLAYS,
  displayRules = null,
  onOpenDetails,
}: LiveTrackingMapProps) {
  const [basemap, setBasemap] = useState<Basemap>('satellite');
  const [showTrail, setShowTrail] = useState(false);
  const [overlays, setOverlays] = useState<AerialOverlayRow[]>([]);
  const [mapReady, setMapReady] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [zonePopup, setZonePopup] = useState<ZonePopup | null>(null);
  const infoTitleId = useId();
  // Esc closes the vehicle card (ST-00270).
  useEffect(() => {
    if (!popupOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopupOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popupOpen]);

  // Raw maplibre instance (captured onLoad — flyTo / fitBounds / getSource live here).
  const glRef = useRef<MaplibreMap | null>(null);
  // Currently-rendered (interpolated) positions.
  const renderedRef = useRef(new Map<string, RenderedPosition>());
  // Active tweens.
  const animRef = useRef(new Map<string, Tween>());
  // Per-vehicle metadata for styling.
  const metaRef = useRef(new Map<string, VehicleMeta>());
  const rafRef = useRef<number | null>(null);
  const selectedRef = useRef<string | null>(selectedRegNo);
  // Latest vehicles, read by the fly-to effect so live socket ticks don't re-trigger a fly on
  // every position update (only selection / flyToNonce do).
  const vehiclesRef = useRef(vehicles);
  useEffect(() => {
    vehiclesRef.current = vehicles;
  }, [vehicles]);
  const statusKeysRef = useRef(statusKeys);
  const keyOf = useCallback(
    (regNo: string) => statusKeysRef.current.get(normaliseRegistration(regNo)) ?? null,
    []
  );
  // ST-00276: the vehicle's display — its board row's, else the organisation's rules over its
  // motion (none without the rules: the motion colours of before).
  const compiled = useMemo(
    () => (displayRules ? compiledOf(displayRules.rules, displayRules.version) : null),
    [displayRules]
  );
  const displaysRef = useRef(displays);
  // Some vehicle on the map carries a pulse mark (the pulse animation runs only then).
  const [pulsing, setPulsing] = useState(false);
  const updatePulsing = useCallback(() => {
    let any = false;
    metaRef.current.forEach((m) => {
      if (m.marks?.dsPulse) any = true;
    });
    setPulsing(any);
  }, []);
  const compiledRef = useRef(compiled);
  const displayOf = useCallback((v: LiveVehiclePosition, now: number): AssetDisplay | null => {
    const own = displaysRef.current.get(normaliseRegistration(v.regNo));
    if (own) return own;
    return compiledRef.current
      ? displayOfMotion(compiledRef.current, deriveStatus(v, now), v.speed ?? null)
      : null;
  }, []);
  // Dwell entry times: regNo -> { geofenceId, entryTime }.
  const dwellMapRef = useRef(new Map<string, { geofenceId: number; entryTime: number }>());
  const hasFitRef = useRef(false);

  // Push the current interpolated positions into both vehicle sources (cheap; small FCs).
  // Returns false if a source isn't there yet (pre-load / mid style-swap).
  const applyData = useCallback(() => {
    const map = glRef.current;
    if (!map) return false;
    const others = map.getSource<GeoJSONSource>(VEHICLES);
    const selected = map.getSource<GeoJSONSource>(SELECTED);
    if (!others || !selected) return false;
    const fcs = buildVehicleCollections(renderedRef.current, metaRef.current, selectedRef.current);
    others.setData(fcs.others);
    selected.setData(fcs.selected);
    return true;
  }, []);

  // Apply now, retrying over a few frames — the declarative sources are (re)added a frame or
  // two after load / basemap swap, so a single attempt can miss.
  const pushData = useCallback(() => {
    let tries = 0;
    const attempt = () => {
      if (applyData() || tries++ > 10) return;
      requestAnimationFrame(attempt);
    };
    attempt();
  }, [applyData]);

  // One animation frame: advance every tween, render, and reschedule while any remain active.
  const tick = useCallback(() => {
    const now = performance.now();
    const anims = animRef.current;
    anims.forEach((a, regNo) => {
      const t = Math.min(1, (now - a.start) / a.duration);
      const e = 1 - Math.pow(1 - t, 3); // easeOutCubic
      renderedRef.current.set(regNo, {
        lng: a.fromLng + (a.toLng - a.fromLng) * e,
        lat: a.fromLat + (a.toLat - a.fromLat) * e,
        heading: lerpAngle(a.fromHeading, a.toHeading, e),
      });
      if (t >= 1) anims.delete(regNo);
    });
    applyData();
    rafRef.current = anims.size > 0 ? requestAnimationFrame(tick) : null;
  }, [applyData]);

  const startRaf = useCallback(() => {
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  // ── Ingest position updates → set up tweens ──
  useEffect(() => {
    const rendered = renderedRef.current;
    const anims = animRef.current;
    const meta = metaRef.current;
    const seen = new Set<string>();

    vehicles.forEach((v) => {
      if (v.lat == null || v.lng == null) return;
      seen.add(v.regNo);
      const heading = v.heading || 0;
      meta.set(v.regNo, vehicleMeta(v, Date.now(), keyOf(v.regNo), displayOf(v, Date.now())));

      const cur = rendered.get(v.regNo);
      if (!cur) {
        // New vehicle — appear in place, no tween.
        rendered.set(v.regNo, { lng: v.lng, lat: v.lat, heading });
        return;
      }
      const moved =
        Math.abs(cur.lng - v.lng) > 1e-7 ||
        Math.abs(cur.lat - v.lat) > 1e-7 ||
        Math.abs((cur.heading || 0) - heading) > 0.5;
      if (moved) {
        anims.set(v.regNo, {
          fromLng: cur.lng,
          fromLat: cur.lat,
          toLng: v.lng,
          toLat: v.lat,
          fromHeading: cur.heading || 0,
          toHeading: heading,
          start: performance.now(),
          duration: ANIM_MS,
        });
      }
    });

    // Drop vehicles that are no longer reporting (or the filter now hides).
    rendered.forEach((_, regNo) => {
      if (!seen.has(regNo)) {
        rendered.delete(regNo);
        anims.delete(regNo);
        meta.delete(regNo);
      }
    });

    pushData();
    updatePulsing();
    if (anims.size > 0) startRaf();
  }, [vehicles, pushData, startRaf, keyOf, displayOf, updatePulsing]);

  // ── A new set of LexiStar statuses (every 30 s): recolour the vehicles ──
  useEffect(() => {
    statusKeysRef.current = statusKeys;
    displaysRef.current = displays;
    compiledRef.current = compiled;
    const now = Date.now();
    vehiclesRef.current.forEach((v) => {
      if (metaRef.current.has(v.regNo))
        metaRef.current.set(v.regNo, vehicleMeta(v, now, keyOf(v.regNo), displayOf(v, now)));
    });
    pushData();
    updatePulsing();
  }, [statusKeys, displays, compiled, keyOf, displayOf, pushData, updatePulsing]);

  // The keys on the map, for the legend.
  const legendKeys = useMemo(() => {
    const now = Date.now();
    return [
      ...new Set(
        vehicles.map(
          (v) =>
            liveStatusOf(
              deriveStatus(v, now),
              statusKeys.get(normaliseRegistration(v.regNo)),
              displays.get(normaliseRegistration(v.regNo)) ??
                (compiled ? displayOfMotion(compiled, deriveStatus(v, now), v.speed ?? null) : null)
            ).key
        )
      ),
    ];
  }, [vehicles, statusKeys, displays, compiled]);

  // ── Aerial overlays (re-fetch when overlaysVersion changes) ──
  useEffect(() => {
    api.aerialOverlays
      .list()
      .then((res: { data?: { data?: AerialOverlayRow[] } }) => setOverlays(res.data?.data || []))
      .catch(() => {});
  }, [overlaysVersion]);

  // Collapse any open trail when the selection changes or clears.
  useEffect(() => {
    setShowTrail(false);
  }, [selectedRegNo]);

  // ── Which vehicles are inside which geofences (occupancy + dwell + popup) ──
  const vehicleZoneMap = useMemo(() => {
    const map = new Map<string, { geofenceId: number; geofenceName: string }>();
    vehicles.forEach((v) => {
      if (!v.lat || !v.lng) return;
      for (const g of geofences) {
        if (isInsideGeofence(v.lat, v.lng, g)) {
          map.set(v.regNo, { geofenceId: g.GeofenceID, geofenceName: g.Name });
          break; // first match wins
        }
      }
    });
    return map;
  }, [vehicles, geofences]);

  useEffect(() => {
    const now = Date.now();
    const dwellMap = dwellMapRef.current;
    vehicles.forEach((v) => {
      const zoneInfo = vehicleZoneMap.get(v.regNo);
      const existing = dwellMap.get(v.regNo);
      if (zoneInfo) {
        if (!existing || existing.geofenceId !== zoneInfo.geofenceId) {
          dwellMap.set(v.regNo, { geofenceId: zoneInfo.geofenceId, entryTime: now });
        }
      } else {
        dwellMap.delete(v.regNo);
      }
    });
  }, [vehicles, vehicleZoneMap]);

  const occupancyCounts = useMemo(() => {
    const counts = new Map<number, number>();
    vehicleZoneMap.forEach((info) =>
      counts.set(info.geofenceId, (counts.get(info.geofenceId) || 0) + 1)
    );
    return counts;
  }, [vehicleZoneMap]);

  const getDwellMinutes = useCallback((regNo: string) => {
    const entry = dwellMapRef.current.get(regNo);
    if (!entry) return null;
    return Math.round((Date.now() - entry.entryTime) / 60000);
  }, []);

  // ── Zones as a GeoJSON polygon source (circular zones → geodesic polygon) ──
  const zonesFC = useMemo((): FeatureCollection<Polygon> => {
    const features: Feature<Polygon>[] = [];
    geofences.forEach((g) => {
      const ring =
        g.GeofenceType === 'POLYGON'
          ? polygonRingFromLatLngs(polygonCoords(g))
          : circleToPolygonRing(g.Longitude, g.Latitude, g.RadiusMeters || 0);
      if (!ring || ring.length < 4) return;
      const meta = getZoneTypeMeta(getZoneType(g));
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: { GeofenceID: g.GeofenceID, color: meta.color, active: g.is_active ? 1 : 0 },
      });
    });
    return { type: 'FeatureCollection', features };
  }, [geofences]);

  // ── Initial camera (used once; fitBounds handles the real framing) ──
  const initialViewStateRef = useRef<{ longitude: number; latitude: number; zoom: number } | null>(
    null
  );
  if (initialViewStateRef.current === null) {
    let longitude = 24.6727;
    let latitude = -28.4793;
    let zoom = 6;
    const first = geofences[0];
    if (vehicles.length > 0) {
      latitude = vehicles.reduce((s, v) => s + v.lat, 0) / vehicles.length;
      longitude = vehicles.reduce((s, v) => s + v.lng, 0) / vehicles.length;
      zoom = 13;
    } else if (first && first.Latitude != null && first.Longitude != null) {
      latitude = first.Latitude;
      longitude = first.Longitude;
      zoom = 14;
    }
    initialViewStateRef.current = { longitude, latitude, zoom };
  }

  // ── Map load: capture instance, draw marker images on demand, prime the sources ──
  const handleLoad = useCallback(
    (e: { target: MaplibreMap }) => {
      const map = e.target;
      glRef.current = map;
      installAssetMarkerImages(map);
      installDisplayMarkImages(map);
      pushData(); // retries until the declarative sources are added
      setMapReady(true);
    },
    [pushData]
  );

  // ST-00276: the pulse marks beat — only while some vehicle carries one (steady when the viewer
  // prefers reduced motion); no timer for a map without pulses.
  usePulseAnimation(mapReady ? glRef.current : null, PULSE_LAYERS, LIVE_MARKER_PX, pulsing);

  // After a basemap swap the style is wiped: re-apply data (react-map-gl re-adds the
  // declarative sources / layers itself, and the marker images come back on demand).
  const handleStyleData = useCallback(() => {
    pushData();
  }, [pushData]);

  // ── fitBounds on first meaningful data ──
  useEffect(() => {
    if (!mapReady || hasFitRef.current) return;
    const map = glRef.current;
    if (!map) return;
    const pts: [number, number][] = [];
    geofences.forEach((g) => {
      if (g.GeofenceType === 'POLYGON') {
        const coords = polygonCoords(g);
        if (coords) coords.forEach(([la, ln]) => pts.push([ln, la]));
      } else if (g.Latitude != null && g.Longitude != null) {
        pts.push([g.Longitude, g.Latitude]);
      }
    });
    vehicles.forEach((v) => {
      if (v.lng != null && v.lat != null) pts.push([v.lng, v.lat]);
    });
    const bounds = boundsFromLngLats(pts);
    if (bounds) {
      map.fitBounds(bounds, { padding: 60, duration: 0, maxZoom: 16 });
      hasFitRef.current = true;
    }
  }, [mapReady, vehicles, geofences]);

  // ── Selection: move the vehicle into the never-clustered source (also handles clearing) ──
  useEffect(() => {
    selectedRef.current = selectedRegNo;
    pushData();
  }, [selectedRegNo, pushData]);

  // ── Fly to selection / recenter request; open the popup ──
  useEffect(() => {
    if (!selectedRegNo) return;
    const map = glRef.current;
    if (!map) return;
    const v = (vehiclesRef.current || []).find((x) => x.regNo === selectedRegNo);
    if (!v || v.lng == null || v.lat == null) return;
    map.flyTo({ center: [v.lng, v.lat], zoom: Math.max(map.getZoom(), SELECT_ZOOM) });
    setPopupOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegNo, flyToNonce]);

  // ── Click: zoom into a bubble, select a vehicle, or open a zone popup ──
  const handleMapClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const f = e.features && e.features[0];
      if (!f) {
        setZonePopup(null);
        setPopupOpen(false); // a click on the empty map closes the vehicle card
        return;
      }
      const layerId = f.layer?.id;
      if (layerId === CLUSTERS) {
        const map = glRef.current;
        const clusterId = Number(f.properties?.cluster_id);
        const geom = f.geometry as Point;
        if (map && Number.isFinite(clusterId)) {
          void zoomIntoCluster(map, VEHICLES, clusterId, geom.coordinates as [number, number]);
        }
      } else if (layerId === 'zones-fill') {
        const gid = f.properties?.GeofenceID;
        const g = geofences.find((x) => String(x.GeofenceID) === String(gid));
        if (g) setZonePopup({ lng: e.lngLat.lng, lat: e.lngLat.lat, geofence: g });
      } else {
        const regNo = f.properties?.regNo;
        if (typeof regNo === 'string') onSelectVehicle?.(regNo);
      }
    },
    [geofences, onSelectVehicle]
  );

  // ── Selected vehicle popup content ──
  const selVeh = selectedRegNo ? vehicles.find((v) => v.regNo === selectedRegNo) : null;
  const selZone = selectedRegNo ? vehicleZoneMap.get(selectedRegNo) : null;
  const selStatus = selVeh
    ? liveStatusOf(deriveStatus(selVeh), keyOf(selVeh.regNo), displayOf(selVeh, Date.now()))
    : null;
  const selStale = selVeh ? isStale(selVeh) : false;
  const selLastUpdate = selVeh?.ts ? new Date(selVeh.ts) : null;
  const selDwell = selVeh ? getDwellMinutes(selVeh.regNo) : null;

  const serverBase =
    (import.meta.env.REACT_APP_API_URL as string | undefined)?.replace('/api', '') ||
    'http://localhost:5002';

  // z-0 keeps the map's stacking context (maplibre panes + the z-[1000] overlays below) under
  // the mobile sidebar drawer (z-60). See ST-00098.
  return (
    <div
      className={`relative z-0 ${isFullscreen ? 'h-full' : 'h-[600px]'} rounded-lg overflow-hidden border border-gray-200`}
    >
      {/* Live count badge */}
      <div className="absolute top-3 right-3 z-[1000] bg-surface/95 backdrop-blur rounded-lg shadow-lg px-3 py-1.5">
        <span className="text-xs font-semibold text-gray-600">
          {vehicles.length} {vehicles.length === 1 ? 'vehicle' : 'vehicles'} on map
        </span>
      </div>

      {/* Bottom-left control cluster: trail toggle (when a vehicle is selected) + basemap toggle */}
      <div className="absolute bottom-3 left-3 z-[1000] flex items-center gap-2">
        {selectedRegNo && (
          <button
            onClick={() => setShowTrail((s) => !s)}
            className={`px-3 py-1.5 rounded-lg shadow-lg text-xs font-medium transition-colors ${
              showTrail
                ? 'bg-teal-500 text-white'
                : 'bg-surface/95 backdrop-blur text-gray-600 hover:bg-gray-100'
            }`}
            title={showTrail ? 'Hide trail' : 'Show trail'}
          >
            {showTrail ? 'Hide Trail' : 'Show Trail'}
          </button>
        )}
        <BasemapToggle basemap={basemap} onChange={setBasemap} />
        <LiveMapLegend keys={legendKeys} legend={displayRules?.legend ?? null} />
      </div>

      <LexiStarMap
        className="absolute inset-0"
        initialViewState={initialViewStateRef.current}
        maxZoom={22}
        basemap={basemap}
        onBasemapChange={setBasemap}
        showBasemapToggle={false}
        navigationPosition="top-left"
        interactiveLayerIds={INTERACTIVE_LAYERS}
        onLoad={handleLoad}
        onStyleData={handleStyleData}
        onClick={handleMapClick}
      >
        {/* Aerial overlays (bottom of the custom stack) */}
        {overlays.map((ov) => (
          <Source
            key={ov.overlay_id}
            id={`aerial-${ov.overlay_id}`}
            type="image"
            url={`${serverBase}${ov.file_path}`}
            coordinates={overlayQuad(ov)}
          >
            <Layer
              id={`aerial-layer-${ov.overlay_id}`}
              type="raster"
              paint={{ 'raster-opacity': ov.opacity ?? 0.8, 'raster-fade-duration': 0 }}
            />
          </Source>
        ))}

        {/* Zones */}
        <Source id="zones" type="geojson" data={zonesFC}>
          <Layer
            id="zones-fill"
            type="fill"
            paint={{ 'fill-color': ['get', 'color'], 'fill-opacity': 0.15 }}
          />
          <Layer
            id="zones-line-active"
            type="line"
            filter={['==', ['get', 'active'], 1]}
            paint={{ 'line-color': ['get', 'color'], 'line-width': 2 }}
          />
          <Layer
            id="zones-line-inactive"
            type="line"
            filter={['==', ['get', 'active'], 0]}
            paint={{
              'line-color': ['get', 'color'],
              'line-width': 2,
              'line-opacity': 0.8,
              'line-dasharray': [2, 2],
            }}
          />
        </Source>

        {/* Occupancy badges (DOM markers so they render on both basemaps). They sit on the
            zone's top edge, above it, so they never cover the vehicles (or the cluster bubbles)
            inside the zone (ST-00244). */}
        {geofences.map((g) => {
          const count = occupancyCounts.get(g.GeofenceID);
          if (!count) return null;
          const [ln, la] = zoneLabelPoint(g);
          if (ln == null || la == null) return null;
          return (
            <Marker
              key={`occ-${g.GeofenceID}`}
              longitude={ln}
              latitude={la}
              anchor="bottom"
              offset={[0, -4]}
              style={{ pointerEvents: 'none' }}
            >
              <div className="bg-teal-500 text-white text-[11px] font-semibold px-1.5 py-0.5 rounded-full shadow border border-white whitespace-nowrap">
                {count === 1 ? '1 vehicle' : `${count} vehicles`}
              </div>
            </Marker>
          );
        })}

        {/* Vehicle trail */}
        {showTrail && selectedRegNo && <TrailLayer regNo={selectedRegNo} />}

        {/* Vehicles (top of the custom stack). Data is applied imperatively via setData(); the
            declarative empty collections are just the re-add seeds. Every vehicle but the
            selected one is clustered below zoom 16. */}
        <Source
          id={VEHICLES}
          type="geojson"
          data={EMPTY_VEHICLES}
          cluster
          clusterMaxZoom={CLUSTER_MAX_ZOOM}
          clusterRadius={CLUSTER_RADIUS}
        >
          {OTHER_MARKS.under.map((l) => (
            <Layer key={l.id} {...l} />
          ))}
          {OTHER_LAYERS.map((l) => (
            <Layer key={l.id} {...l} />
          ))}
          {OTHER_MARKS.over.map((l) => (
            <Layer key={l.id} {...l} />
          ))}
          <Layer {...CLUSTER_LAYER} />
        </Source>

        {/* The selected vehicle: never clustered, ringed, on top. */}
        <Source id={SELECTED} type="geojson" data={EMPTY_FC}>
          <Layer {...SELECTED_RING} />
          {SELECTED_MARKS.under.map((l) => (
            <Layer key={l.id} {...l} />
          ))}
          {SELECTED_LAYERS.map((l) => (
            <Layer key={l.id} {...l} />
          ))}
          {SELECTED_MARKS.over.map((l) => (
            <Layer key={l.id} {...l} />
          ))}
        </Source>

        {/* Selected-vehicle card (ST-00270): name, status (the shared colours) and speed, live;
            Details opens the detail panel. Esc or a click on the empty map closes it. */}
        {popupOpen && selVeh && selVeh.lng != null && selVeh.lat != null && selStatus && (
          <Popup
            longitude={selVeh.lng}
            latitude={selVeh.lat}
            anchor="bottom"
            offset={22}
            closeOnClick={false}
            onClose={() => setPopupOpen(false)}
            className={ASSET_INFO_POPUP_CLASS}
            maxWidth="280px"
          >
            <div role="dialog" aria-labelledby={infoTitleId} data-testid="live-asset-info">
              <AssetInfoCard
                titleId={infoTitleId}
                name={selVeh.regNo}
                statusKey={selStatus.key}
                statusLabel={selStatus.label}
                motionText={
                  selStatus.motion === 'moving'
                    ? 'moving'
                    : selStatus.motion === 'offline'
                      ? 'no recent position'
                      : 'standing'
                }
                speedKmh={selStale ? null : (selVeh.speed ?? 0)}
                when={
                  selLastUpdate
                    ? `Last report ${selLastUpdate.toLocaleTimeString('en-GB', { timeZone: 'Africa/Johannesburg' })}${selStale ? ' (stale)' : ''}`
                    : null
                }
                whenStale={selStale}
              >
                {/* ST-00276: every mark, in words (the map draws the strongest of each kind). */}
                <DisplayMarks overlays={selStatus.display?.overlays} className="mt-1.5" />
                {selZone && (
                  <p className="mt-1 text-xs font-medium text-teal-700">
                    In {selZone.geofenceName} for {selDwell != null ? `${selDwell} min` : '--'}
                  </p>
                )}
                {onOpenDetails && (
                  <button
                    type="button"
                    onClick={() => onOpenDetails(selVeh.regNo)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-600 text-white hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    Details
                  </button>
                )}
              </AssetInfoCard>
            </div>
          </Popup>
        )}

        {/* Zone popup */}
        {zonePopup && (
          <Popup
            longitude={zonePopup.lng}
            latitude={zonePopup.lat}
            anchor="bottom"
            closeOnClick={false}
            onClose={() => setZonePopup(null)}
            maxWidth="240px"
          >
            <div className="text-sm min-w-[160px]">
              <p className="font-bold text-base mb-1">{zonePopup.geofence.Name}</p>
              <p className="mb-1">
                <ZoneTypeBadge type={getZoneType(zonePopup.geofence)} />
              </p>
              {zonePopup.geofence.LinkedLocationName && (
                <p className="text-xs text-teal-600 mb-1">
                  Site: {zonePopup.geofence.LinkedLocationName}
                </p>
              )}
              {zonePopup.geofence.description && (
                <p className="text-gray-500 mb-1">{zonePopup.geofence.description}</p>
              )}
              {zonePopup.geofence.GeofenceType === 'POLYGON' ? (
                <p className="text-xs text-gray-400">Polygon zone</p>
              ) : (
                <p className="text-xs text-gray-400">Radius: {zonePopup.geofence.RadiusMeters}m</p>
              )}
              {zonePopup.geofence.DwellTimeMinutes && (
                <p className="text-xs">Dwell alert: {zonePopup.geofence.DwellTimeMinutes} min</p>
              )}
            </div>
          </Popup>
        )}
      </LexiStarMap>
    </div>
  );
}
